import { api, requestBlob } from '../../api/client';
import type { ArchivedFilter, ComponentBrief } from '../components/api';

export type SpecStatus = 'draft' | 'released' | 'deprecated';

export interface VersionBrief {
  id: string;
  code: string;
  version: number;
  status: SpecStatus;
  /** Set at release. Equal hashes mean two versions say the same thing. */
  content_hash: string | null;
  released_at: string | null;
  cloned_from_id: string | null;
  note: string | null;
}

export interface ChildVersionBrief extends Omit<VersionBrief, 'content_hash' | 'released_at' | 'cloned_from_id' | 'note'> {
  specification_id: string;
}

export interface Alternative {
  id: string;
  component_id: string;
  note: string | null;
  component: ComponentBrief | null;
}

export interface SpecLine {
  id: string;
  line_no: number;
  reference_designators: string | null;
  component_id: string | null;
  /** A sub-assembly, pinned to a version so it cannot shift underneath. */
  child_version_id: string | null;
  quantity: string;
  is_optional: boolean;
  note: string | null;
  component: ComponentBrief | null;
  child_version: ChildVersionBrief | null;
  alternatives: Alternative[];
}

export interface SpecVersion extends VersionBrief {
  specification_id: string;
  lines: SpecLine[];
}

export interface Specification {
  id: string;
  code: string | null;
  name: string;
  description: string | null;
  /** Where the BOM came from: the repository, the project page, the article.
   *  Almost none of them are invented here. */
  source_url: string | null;
  output_component_id: string | null;
  output_component: ComponentBrief | null;
  versions: VersionBrief[];
  /** Set when it was taken out of the list. */
  archived_at: string | null;
}

export interface LineInput {
  component_id?: string | null;
  child_version_id?: string | null;
  quantity: string;
  reference_designators?: string | null;
  is_optional?: boolean;
  note?: string | null;
}

export interface ImportCandidate {
  component_id: string;
  internal_code: string;
  name: string;
}

export interface ImportRow {
  row_no: number;
  query: string;
  designators: string | null;
  quantity: string | null;
  note: string | null;
  is_optional: boolean;
  component_id: string | null;
  component_code: string | null;
  component_name: string | null;
  /** How far to trust the match: an exact code is the user's own answer, a
   *  suggestion is a proposal they have to accept. */
  match: 'exact_code' | 'exact_mpn' | 'suggested' | 'none';
  candidates: ImportCandidate[];
  error: string | null;
}

export interface ImportPreview {
  rows: ImportRow[];
  matched: number;
  unmatched: number;
  committed: boolean;
}

export interface ComparisonLine {
  id: string;
  line_no: number;
  target_kind: 'component' | 'assembly';
  target_id: string;
  target_label: string;
  child_version_id: string | null;
  quantity: string;
  designators: string | null;
  is_optional: boolean;
  alternatives: string[];
}

export interface ComparisonRow {
  state: 'unchanged' | 'changed' | 'added' | 'removed';
  before: ComparisonLine | null;
  after: ComparisonLine | null;
  changes: string[];
}

export interface ExplodedComparisonRow {
  component_id: string;
  internal_code: string;
  name: string;
  before: string;
  after: string;
  state: 'unchanged' | 'changed' | 'added' | 'removed';
}

export interface BomComparison {
  before_version_id: string;
  after_version_id: string;
  include_optional: boolean;
  rows: ComparisonRow[];
  exploded: ExplodedComparisonRow[];
}

/** Archived ones are out of the way by default and reachable on request: one
 *  archived by mistake is otherwise gone with no way even to look. */
export const listSpecs = (archived: ArchivedFilter = 'hide') =>
  api.get<Specification[]>(`/specs${archived === 'hide' ? '' : `?archived=${archived}`}`);

export const restoreSpec = (id: string) => api.post<Specification>(`/specs/${id}/restore`);

export const getSpec = (id: string) => api.get<Specification>(`/specs/${id}`);

export interface SpecInput {
  name: string;
  code?: string | null;
  description?: string | null;
  source_url?: string | null;
  output_component_id?: string | null;
}

export const createSpec = (input: SpecInput) => api.post<Specification>('/specs', input);

export const updateSpec = (id: string, input: Partial<SpecInput>) =>
  api.patch<Specification>(`/specs/${id}`, input);

export const archiveSpec = (id: string) => api.delete<void>(`/specs/${id}`);

/** Cloning is how a released BOM is corrected — never by editing it. */
export const createVersion = (specId: string, cloneFromId?: string | null) =>
  api.post<SpecVersion>(`/specs/${specId}/versions`, { clone_from_id: cloneFromId ?? null });

export const getVersion = (id: string) => api.get<SpecVersion>(`/spec-versions/${id}`);

export const deleteVersion = (id: string) => api.delete<void>(`/spec-versions/${id}`);

export const releaseVersion = (id: string) =>
  api.post<SpecVersion>(`/spec-versions/${id}/release`);

export const deprecateVersion = (id: string) =>
  api.post<SpecVersion>(`/spec-versions/${id}/deprecate`);

export const addLine = (versionId: string, input: LineInput) =>
  api.post<SpecLine>(`/spec-versions/${versionId}/lines`, input);

export const updateLine = (versionId: string, lineId: string, input: Partial<LineInput> & { line_no?: number }) =>
  api.patch<SpecLine>(`/spec-versions/${versionId}/lines/${lineId}`, input);

export const deleteLine = (versionId: string, lineId: string) =>
  api.delete<void>(`/spec-versions/${versionId}/lines/${lineId}`);

export const addAlternative = (
  versionId: string,
  lineId: string,
  input: { component_id: string; note?: string | null },
) => api.post<Alternative>(`/spec-versions/${versionId}/lines/${lineId}/alternatives`, input);

export const deleteAlternative = (versionId: string, lineId: string, id: string) =>
  api.delete<void>(`/spec-versions/${versionId}/lines/${lineId}/alternatives/${id}`);

/**
 * Read a BOM file. With `commit` false nothing is written — the answer is a
 * preview of what each row matched, which is the whole point: a silent import
 * that guesses wrong fills the catalogue with the wrong parts.
 */
export async function importBom(
  versionId: string,
  file: File,
  options?: { commit?: boolean; resolutions?: { row_no: number; component_id: string | null }[]; replace?: boolean; skipUnmatched?: boolean },
): Promise<ImportPreview> {
  const body = new FormData();
  body.append('file', file);
  body.append('commit', String(options?.commit ?? false));
  if (options?.commit) {
    body.append(
      'options',
      JSON.stringify({
        resolutions: options.resolutions ?? [],
        replace: options.replace ?? true,
        skip_unmatched: options.skipUnmatched ?? false,
      }),
    );
  }
  return api.postForm<ImportPreview>(`/spec-versions/${versionId}/import`, body);
}

export const exportUrl = (versionId: string, format: 'csv' | 'xlsx') =>
  `/spec-versions/${versionId}/export?format=${format}`;

export const downloadBom = (versionId: string, format: 'csv' | 'xlsx') =>
  requestBlob(exportUrl(versionId, format));

export const compareBom = (
  specId: string,
  before: string,
  after: string,
  options: { includeOptional: boolean; differencesOnly: boolean },
) => {
  const query = new URLSearchParams({
    before_version_id: before,
    after_version_id: after,
    include_optional: String(options.includeOptional),
    differences_only: String(options.differencesOnly),
  });
  return api.get<BomComparison>(`/specs/${specId}/compare?${query}`);
};

export const comparisonUrl = (
  specId: string, before: string, after: string,
  options: { includeOptional: boolean; differencesOnly: boolean },
) => `/specs/${specId}/compare.xlsx?${new URLSearchParams({
  before_version_id: before,
  after_version_id: after,
  include_optional: String(options.includeOptional),
  differences_only: String(options.differencesOnly),
})}`;

export const downloadComparison = (
  specId: string, before: string, after: string,
  options: { includeOptional: boolean; differencesOnly: boolean },
) => api.blob(comparisonUrl(specId, before, after, options));
